import {
  MAINTENANCE_LEAD_MS,
  STATUS_PAGE_URL,
  statusIssueUrl,
} from "./status-feed.constants";

/**
 * Normalised view of Hund's `/live/v2/status_page` SSE feed.
 *
 * The wire shapes below follow Hund's own client (`hundio/status.js`,
 * `src/status.coffee`), cross-checked against the live stream (curl'd
 * 2026-08-12). Timestamps on the wire are UNIX **seconds**, not milliseconds.
 *
 * The feed maintains two independent things: an overall `state` string, and a
 * set of active issues. Basic events change only the former, issue events only
 * the latter — so this model takes them as two separate inputs rather than
 * pretending there is one snapshot object.
 */

/** Overall page state. `pending` (Hund's own null-state fallback) and anything
 * else are treated as unknown → fail silent. */
export type OverallState = "operational" | "degraded" | "outage";

export interface StatusFeedIssueComponent {
  id?: string;
  name?: string;
}

export interface StatusFeedIssueUpdate {
  id?: string;
  issue_id?: string;
  body?: string;
  label?: string;
  /** UNIX seconds. */
  created_at?: number;
  /** Present on `issue_reopened`, which carries the whole issue back. */
  issue?: StatusFeedIssue;
}

export interface StatusFeedIssue {
  id?: string;
  title?: string;
  body?: string;
  /** Long-running "standing" issue rather than a fresh incident. */
  standing?: boolean;
  /** Planned maintenance rather than an unplanned incident. */
  scheduled?: boolean;
  /** UNIX seconds — the maintenance window start, present when `scheduled`. */
  starts_at?: number;
  /** UNIX seconds. */
  ends_at?: number;
  /** UNIX seconds. */
  created_at?: number;
  /** Newest-first. Hund unshifts each new update onto the front. */
  updates?: StatusFeedIssueUpdate[];
  /** The components this issue impacts — impact lives ON the issue. */
  components?: StatusFeedIssueComponent[];
}

/** Dot colour. `none` means "render nothing" — the fail-silent state. */
export type StatusDotTone = "green" | "amber" | "red" | "blue" | "none";

export type BannerState = "none" | "degraded" | "outage";

export interface ServiceStatusIncident {
  id: string;
  title: string;
  /** Public page for this incident. */
  url: string;
  /** Body of the newest update, falling back to the issue body. */
  latestUpdate?: string;
  /** Names of the components this issue impacts. */
  components: string[];
  scheduled: boolean;
}

export interface ServiceStatusMaintenance extends ServiceStatusIncident {
  /** UNIX milliseconds. */
  startsAt?: number;
  /** True once the window has opened (or it is a standing issue). */
  active: boolean;
}

export interface ServiceStatusView {
  overall: OverallState | "unknown";
  dot: StatusDotTone;
  bannerState: BannerState;
  /** Suffix for the "System status · …" menu item. Empty when unknown. */
  label: string;
  /** True when the feed is unreachable, pending, or unparseable — show nothing. */
  isError: boolean;
  /**
   * Identity of what is currently being shown. Session banner dismissal is
   * keyed by this, so a new or escalated incident re-shows a dismissed banner.
   */
  bannerKey: string;
  /** The incident driving the banner: the first non-scheduled active issue. */
  incident?: ServiceStatusIncident;
  /** Active or imminent scheduled maintenance. Never drives the banner. */
  maintenance?: ServiceStatusMaintenance;
  /** Every active issue, for the popover. */
  issues: ServiceStatusIncident[];
  /** Deduped component names impacted across all active issues. */
  affectedComponents: string[];
}

const OPERATIONAL = "operational";

const OVERALL_STATES = new Set<string>([OPERATIONAL, "degraded", "outage"]);

/** The fail-silent view: no colour, no banner, never implies healthy. */
export const UNKNOWN_STATUS_VIEW: ServiceStatusView = {
  overall: "unknown",
  dot: "none",
  bannerState: "none",
  label: "",
  isError: true,
  bannerKey: "unknown",
  issues: [],
  affectedComponents: [],
};

function toIncident(
  issue: StatusFeedIssue,
  index: number
): ServiceStatusIncident {
  const id = issue.id ?? `issue-${index}`;
  // `updates` is newest-first, so the head is the latest word on the incident.
  const latest = issue.updates?.[0]?.body;
  return {
    id,
    title: issue.title?.trim() || "Service incident",
    url: statusIssueUrl(id),
    latestUpdate: latest || issue.body || undefined,
    components: (issue.components ?? [])
      .map((component) => component.name?.trim())
      .filter((name): name is string => Boolean(name)),
    scheduled: Boolean(issue.scheduled),
  };
}

/**
 * Whether a scheduled issue is close enough to matter: already running, a
 * standing issue, or starting within the lead window. A scheduled issue with
 * no `starts_at` is treated as running — it can only ever add a blue badge,
 * never hide an incident.
 */
function isMaintenanceRelevant(issue: StatusFeedIssue, now: number): boolean {
  if (!issue.scheduled) return false;
  if (issue.standing) return true;
  if (typeof issue.starts_at !== "number") return true;
  return issue.starts_at * 1000 - now <= MAINTENANCE_LEAD_MS;
}

/**
 * Pure mapping from the feed's two independent inputs — the overall state
 * string and the active issue list — to everything the UI renders. No React,
 * no DOM, no clock of its own: `now` is injected so the maintenance window is
 * testable.
 *
 * The overall `state` is authoritative for the dot and banner. We never
 * recompute it from issues or components, because Hund already applies the
 * page's own roll-up rules.
 *
 * Precedence is outage > degraded > maintenance > operational. An active
 * incident outranks a maintenance window that happens to overlap it, otherwise
 * a blue "maintenance" dot would understate a live outage.
 */
export function deriveStatusView(
  state: string | null | undefined,
  issues: StatusFeedIssue[] = [],
  now: number = Date.now()
): ServiceStatusView {
  if (!OVERALL_STATES.has(state ?? "")) {
    return UNKNOWN_STATUS_VIEW;
  }

  const overall = state as OverallState;
  const incidents = issues.map((issue, index) => toIncident(issue, index));

  const affectedComponents = [
    ...new Set(incidents.flatMap((incident) => incident.components)),
  ];

  const incident = incidents.find((candidate) => !candidate.scheduled);

  const maintenanceIndex = issues.findIndex((issue) =>
    isMaintenanceRelevant(issue, now)
  );
  const maintenance = (() => {
    if (maintenanceIndex === -1) return;
    const issue = issues[maintenanceIndex];
    const startsAt =
      typeof issue.starts_at === "number" ? issue.starts_at * 1000 : undefined;
    return {
      ...incidents[maintenanceIndex],
      startsAt,
      active:
        Boolean(issue.standing) || startsAt === undefined || startsAt <= now,
    } satisfies ServiceStatusMaintenance;
  })();

  if (overall === "outage" || overall === "degraded") {
    const isOutage = overall === "outage";
    return {
      overall,
      dot: isOutage ? "red" : "amber",
      bannerState: overall,
      label: isOutage ? "Outage" : "Degraded",
      isError: false,
      bannerKey: incident?.id ?? overall,
      incident: incident ??
        // The page can go non-operational off component state alone, with no
        // issue written yet. Say something true rather than nothing.
        {
          id: overall,
          title: isOutage
            ? "Some Sked services are down"
            : "Some Sked services are degraded",
          // No real issue id, so there is no /issues/… page to point at.
          url: STATUS_PAGE_URL,
          components: [],
          scheduled: false,
        },
      maintenance,
      issues: incidents,
      affectedComponents,
    };
  }

  if (maintenance) {
    return {
      overall,
      dot: "blue",
      bannerState: "none",
      label: maintenance.active
        ? "Maintenance in progress"
        : "Maintenance scheduled",
      isError: false,
      bannerKey: maintenance.id,
      maintenance,
      issues: incidents,
      affectedComponents,
    };
  }

  return {
    overall,
    dot: "green",
    bannerState: "none",
    label: "Operational",
    isError: false,
    bannerKey: OPERATIONAL,
    issues: incidents,
    affectedComponents,
  };
}
